import {
  type CampaignGoalKind,
  campaignGoalKindSchema,
} from "../../../skills/leadcue-reply/scripts/lib/campaign-config.ts";

/*
 * What the inbox may say about a campaign's goal, per goal kind (docs/2026-09-21-inbox-morning-view.md,
 * work item 2). This replaces a `bookable` predicate that was true for two kinds and hard-coded the
 * word "booked" into rows belonging to five, so a contribution campaign's rep read "Agreed, not
 * booked" about a goal that is not a booking.
 *
 * The table is keyed by `CampaignGoalKind`, so adding a goal fails the typecheck here until someone
 * answers it, rather than silently inheriting a default. It carries wording and thresholds only; it
 * grants nothing and decides no transition. Which completions can be recorded is not this table's
 * opinion either: `available` must match the goals `mark_booked` accepts in `work-plan.ts`, and
 * `tests/goal-lifecycle.test.ts` holds the two to each other against the real reducer.
 */

/**
 * Whether a completion can be recorded for this goal today, and the word the inbox uses for it.
 * `unavailable` carries its reason, in the shape the ask-to-action matrix already uses for a
 * receipt that does not exist: nothing is invented for a goal whose evidence nobody has chosen.
 */
type GoalCompletion =
  | {
      readonly status: "available";
      readonly completed: string;
      readonly missing: string;
      /** The question the completion card asks, so the rep answers it by choosing, never typing. */
      readonly question: string;
    }
  | { readonly status: "unavailable"; readonly reason: string };

export type GoalLifecycle = {
  /** The word for the lead's commitment, or null where the inbox claims no commitment state. */
  readonly commitment: string | null;
  readonly completion: GoalCompletion;
  /**
   * How long a standing commitment may go uncompleted before the row carries a reminder, or null
   * where no completion can be recorded — a clock nothing can stop is not a reminder, it is nagging.
   */
  readonly reminderAfterMs: number | null;
};

/** After this long committed with no completion, the row carries a reminder and sorts first. */
export const COMMITMENT_REMINDER_MS = 7 * 24 * 60 * 60 * 1000;

/*
 * A commercial exploration, a contribution enquiry and a resource delivery each end in something
 * that happens off this system, and no receipt for them has been selected. The pilot records
 * meetings and event registrations only (`mark_booked`), so those three say so and stop there.
 */
const noReceipt = (goal: string): GoalCompletion => ({
  status: "unavailable",
  reason: `No completion receipt exists for ${goal}; which evidence counts is a product decision that has not been made.`,
});

export const GOAL_LIFECYCLE: Record<CampaignGoalKind, GoalLifecycle> = {
  phone_call: {
    commitment: null,
    completion: {
      status: "unavailable",
      reason: "Call readiness is tracked separately from booking completion.",
    },
    reminderAfterMs: null,
  },
  meeting: {
    commitment: "Agreed",
    completion: {
      status: "available",
      completed: "booked",
      missing: "not booked",
      question: "Is the meeting booked?",
    },
    reminderAfterMs: COMMITMENT_REMINDER_MS,
  },
  event_participation: {
    commitment: "Agreed",
    completion: {
      status: "available",
      completed: "registered",
      missing: "not registered",
      question: "Is the lead registered?",
    },
    reminderAfterMs: COMMITMENT_REMINDER_MS,
  },
  commercial_exploration: {
    commitment: "Agreed",
    completion: noReceipt("a commercial exploration"),
    reminderAfterMs: null,
  },
  contribution_research: {
    commitment: "Agreed",
    completion: noReceipt("a contribution enquiry"),
    reminderAfterMs: null,
  },
  resource_delivery: {
    commitment: "Agreed",
    completion: noReceipt("a resource delivery"),
    reminderAfterMs: null,
  },
};

/**
 * A plan whose campaign has no configured goal. `WorkPlan.goalKind` is nullable and a plan projected
 * from a campaign without a goal carries null, so this is a real row in the inbox, not a fallback:
 * with no goal there is nothing to be committed toward, and the inbox claims neither state.
 */
export const UNSET_GOAL_LIFECYCLE: GoalLifecycle = {
  commitment: null,
  completion: {
    status: "unavailable",
    reason: "The campaign has no configured goal, so no completion is defined.",
  },
  reminderAfterMs: null,
};

/** The lifecycle for a plan's goal, including the plan whose campaign configured none. */
export function goalLifecycle(kind: CampaignGoalKind | null): GoalLifecycle {
  return kind === null ? UNSET_GOAL_LIFECYCLE : GOAL_LIFECYCLE[kind];
}

/** Every goal kind, for tests and for any caller that must cover all of them. */
export const GOAL_KINDS: readonly CampaignGoalKind[] = campaignGoalKindSchema.options;

/**
 * Whether a standing commitment toward this goal can ever be resolved, which is what makes
 * "committed, not completed" a state worth showing: without a receipt the row would never leave it.
 */
export function tracksCompletion(kind: CampaignGoalKind | null): boolean {
  return goalLifecycle(kind).completion.status === "available";
}
